import { ApolloClient } from '@apollo/client'
import gql from 'graphql-tag'
import cloneDeep from 'lodash/cloneDeep'

import {
  PagePresentation,
  PageType,
  SinglePostQuery as SinglePostQueryType,
  SinglePostQueryVariables,
} from '@Generated/graphql'
import BlockContent from '@Queries/cms/fragments/BlockContent.fragment'
import Fields from '@Queries/cms/fragments/Fields.fragment'
import MoneyContent from '@Queries/cms/fragments/MoneyContent.fragment'
import OpenGraphContent from '@Queries/cms/fragments/OpenGraphContent.fragment'
import ProductContent from '@Queries/cms/fragments/ProductContent.fragment'
import ReferenceContent from '@Queries/cms/fragments/ReferenceContent.fragment'
import WordbookFragment from '@Queries/wordbook/wordbook.fragment'

export const SinglePostQuery = gql`
  ${ProductContent}
  ${OpenGraphContent}
  ${MoneyContent}
  ${ReferenceContent}
  ${Fields}
  ${BlockContent}
  ${WordbookFragment}

  query SinglePost(
    $type: PageType!
    $slug: String
    $id: ID
    $breadcrumbsVersion: Int = 1
    $presentation: PagePresentation = FULL
    $boost: ID
  ) {
    page(type: $type, slug: $slug, id: $id, presentation: $presentation) {
      id
      type
      title
      url
      brand {
        id
        logo
        title
      }
      seo {
        title
        description
        noIndex
        includeInSitemap
      }
      breadcrumbs(version: $breadcrumbsVersion) {
        id
        type
        title
        url
      }
      openGraph {
        ...OpenGraphContent
      }
      alternateLinks {
        locale
        url
      }
      blocks {
        ...BlockContent
        ... on GlobalBlock {
          id
          type
          blocks {
            ...BlockContent
          }
        }
        ... on ProductListBlock {
          extraBlocks {
            ...BlockContent
          }
          injections {
            id
            slot
            blocks {
              ...BlockContent
            }
          }
          guidedFilters {
            ... on GuidedFilterAttributeEnum {
              items {
                extraBlocks {
                  ...BlockContent
                }
                injections {
                  id
                  slot
                  blocks {
                    ...BlockContent
                  }
                }
              }
            }
          }
        }
      }
      fields {
        ...Fields
      }
    }
  }
`

export const getSinglePost = async (
  apolloClient: ApolloClient<object>,
  type: PageType,
  slug: string,
  id?: string,
  boost?: string,
) => {
  const response = await apolloClient.query<SinglePostQueryType, SinglePostQueryVariables>({
    query: SinglePostQuery,
    variables: {
      boost,
      breadcrumbsVersion: 2,
      id,
      slug,
      type,
    },
  })

  // TODO: hack to avoid apollo-client's frozen objects in dev mode
  if (Object.isFrozen(response?.data)) {
    // eslint-disable-next-line no-param-reassign
    return cloneDeep(response)
  }

  return response
}

export const getSinglePostByID = async (
  apolloClient: ApolloClient<object>,
  type: PageType,
  id: string,
  presentation: PagePresentation = PagePresentation.Full,
  boost?: string,
) => {
  const response = await apolloClient.query<SinglePostQueryType, SinglePostQueryVariables>({
    query: SinglePostQuery,
    variables: {
      boost,
      breadcrumbsVersion: 2,
      id,
      presentation,
      type,
    },
  })

  // TODO: hack to avoid apollo-client's frozen objects in dev mode
  if (Object.isFrozen(response?.data)) {
    // eslint-disable-next-line no-param-reassign
    return cloneDeep(response)
  }

  return response
}
